import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { motion, useMotionValue, useSpring } from 'framer-motion';

// ─── Playful Bold Dark Palette ────────────────────────────────────────────────
const COLOR_MAP = {
  primary:   { neon: '#22D3EE', dim: '#0891B2', grad: ['#22D3EE', '#3B82F6'], glow: 'rgba(34,211,238,0.35)' },
  success:   { neon: '#38BDF8', dim: '#0284C7', grad: ['#38BDF8', '#2563EB'], glow: 'rgba(56,189,248,0.35)' },
  warning:   { neon: '#60A5FA', dim: '#1D4ED8', grad: ['#60A5FA', '#3B82F6'], glow: 'rgba(96,165,250,0.35)' },
  error:     { neon: '#818CF8', dim: '#4F46E5', grad: ['#818CF8', '#3B82F6'], glow: 'rgba(129,140,248,0.35)' },
  info:      { neon: '#93C5FD', dim: '#2563EB', grad: ['#93C5FD', '#3B82F6'], glow: 'rgba(147,197,253,0.35)' },
  secondary: { neon: '#60A5FA', dim: '#1E40AF', grad: ['#60A5FA', '#818CF8'], glow: 'rgba(96,165,250,0.35)' },
};

const getC = (color) => COLOR_MAP[color] || COLOR_MAP.primary;

// ─── Inject animations ────────────────────────────────────────────────────────
const STAT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=JetBrains+Mono:wght@500;600;700&family=DM+Sans:wght@400;500;600&display=swap');

  @keyframes float-chip { 0%,100% { transform: translateY(0px) rotate(-1deg); } 50% { transform: translateY(-4px) rotate(1deg); } }
  @keyframes spin-ring  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes ping-dot   { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: 0.3; } }
  @keyframes bar-fill   { from { width: 0%; } to { width: var(--bar-w); } }
  @keyframes number-pop { 0% { transform: scale(0.8); opacity: 0; } 70% { transform: scale(1.06); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes card-shimmer {
    0%   { left: -100%; }
    100% { left: 200%; }
  }
  @keyframes hue-rotate {
    0%   { filter: hue-rotate(0deg); }
    100% { filter: hue-rotate(30deg); }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('stat-css-v2')) {
  const s = document.createElement('style');
  s.id = 'stat-css-v2';
  s.innerHTML = STAT_CSS;
  document.head.appendChild(s);
}

// ─── Count-up hook ────────────────────────────────────────────────────────────
const useCountUp = (target, duration = 1100) => {
  const [count, setCount] = useState(0);
  const frame = useRef(null);
  const start = useRef(null);
  const isNum =
    typeof target === 'number' ||
    (typeof target === 'string' && /^\d[\d,]*$/.test(String(target).replace(/,/g, '')));
  const numVal = isNum ? parseInt(String(target).replace(/,/g, ''), 10) : 0;

  useEffect(() => {
    if (!isNum) { setCount(numVal); return; }
    setCount(0); start.current = null;
    const step = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min((ts - start.current) / duration, 1);
      const e = 1 - Math.pow(1 - p, 4);
      setCount(Math.floor(e * numVal));
      if (p < 1) frame.current = requestAnimationFrame(step);
      else setCount(numVal);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [numVal, duration, isNum]);

  return isNum ? count.toLocaleString() : String(target);
};

// ─── Tilt card wrapper ────────────────────────────────────────────────────────
const TiltCard = ({ children, isActive, C, sx, onClick }) => {
  const ref = useRef(null);
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const sRotX = useSpring(rotX, { stiffness: 300, damping: 30 });
  const sRotY = useSpring(rotY, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    rotX.set(y * -8);
    rotY.set(x * 8);
  };
  const handleMouseLeave = () => { rotX.set(0); rotY.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX: sRotX, rotateY: sRotY, transformStyle: 'preserve-3d', transformPerspective: 800, width: '100%', display: 'flex' }}
      whileTap={onClick ? { scale: 0.97 } : {}}
      onClick={onClick}
      sx={sx}
    >
      {children}
    </motion.div>
  );
};

// ─── Decorative Rings ─────────────────────────────────────────────────────────
const SpinRing = ({ color, size, duration, delay, opacity = 0.18, dash = '6,10' }) => (
  <Box sx={{
    position: 'absolute',
    width: size, height: size,
    borderRadius: '50%',
    border: `1.5px dashed ${alpha(color, opacity)}`,
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: `spin-ring ${duration}s linear ${delay}s infinite`,
    pointerEvents: 'none',
    zIndex: 0,
  }} />
);

// ─── Icon Box ─────────────────────────────────────────────────────────────────
const IconBox = ({ Icon, C, isActive, index }) => (
  <Box sx={{
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <SpinRing color={C.neon} size={64} duration={8} delay={index * 0.4} opacity={isActive ? 0.28 : 0.12} />
    <SpinRing color={C.neon} size={80} duration={13} delay={index * 0.6} opacity={isActive ? 0.16 : 0.07} dash="3,14" />

    <motion.div
      whileHover={{ rotate: [0, -8, 8, 0], scale: 1.12 }}
      transition={{ duration: 0.4 }}
    >
      <Box sx={{
        borderRadius: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isActive
          ? `linear-gradient(145deg, ${alpha(C.neon, 0.28)}, ${alpha(C.dim, 0.2)})`
          : `linear-gradient(145deg, ${alpha(C.neon, 0.14)}, ${alpha(C.dim, 0.08)})`,
        border: `1.5px solid ${alpha(C.neon, isActive ? 0.5 : 0.25)}`,
        boxShadow: isActive
          ? `0 0 0 4px ${alpha(C.neon, 0.1)}, 0 0 20px ${alpha(C.neon, 0.35)}, inset 0 1px 0 ${alpha(C.neon, 0.3)}`
          : `inset 0 1px 0 ${alpha(C.neon, 0.15)}`,
        color: C.neon,
        transition: 'all 0.25s ease',
        zIndex: 1,
        position: 'relative',
        width: { xs: 44, sm: 52 },
        height: { xs: 44, sm: 52 },
      }}>
        <Icon sx={{ fontSize: { xs: isActive ? 22 : 20, sm: isActive ? 26 : 23 }, filter: isActive ? `drop-shadow(0 0 6px ${C.neon})` : 'none', transition: 'all 0.25s ease' }} />
      </Box>
    </motion.div>
  </Box>
);

// ─── Single Stat Card ─────────────────────────────────────────────────────────
const StatCard = ({
  icon: Icon,
  value,
  label,
  color    = 'primary',
  flex     = 1,
  trend,
  progressValue,
  index    = 0,
  onClick,
  isActive = false,
  compact  = false,
  valueSize = 'default',
}) => {
  const C            = getC(color);
  const displayValue = useCountUp(value);

  return (
    <Box sx={{
      width: '100%',
      minWidth: 0,
      display: 'flex',
    }}>
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      style={{
        width: '100%',
        display: 'flex',
      }}
    >
      <TiltCard isActive={isActive} C={C} onClick={onClick}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: '18px',
            position: 'relative',
            overflow: 'hidden',
            cursor: onClick ? 'pointer' : 'default',
            background: isActive
              ? `linear-gradient(145deg,
                  ${alpha('#0A1224', 0.98)} 0%,
                  ${alpha('#060D1A', 0.96)} 100%)`
              : `linear-gradient(145deg,
                  ${alpha('#0E192E', 0.96)} 0%,
                  ${alpha('#060D1A', 0.94)} 100%)`,
            border: isActive
              ? `1.5px solid ${alpha(C.neon, 0.5)}`
              : `1px solid ${alpha(C.neon, 0.12)}`,
            boxShadow: isActive
              ? `0 0 0 1px ${alpha(C.neon, 0.12)},
                 0 20px 60px ${alpha('#000', 0.6)},
                 0 0 40px ${alpha(C.neon, 0.2)},
                 inset 0 1px 0 ${alpha(C.neon, 0.2)},
                 inset 0 -1px 0 ${alpha(C.dim, 0.1)}`
              : `0 12px 40px ${alpha('#000', 0.4)},
                 inset 0 1px 0 ${alpha('#ffffff', 0.04)}`,
            transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {/* Shimmer sweep on hover */}
          <Box sx={{
            position: 'absolute',
            top: 0, bottom: 0,
            width: '60%',
            background: `linear-gradient(90deg, transparent, ${alpha(C.neon, 0.04)}, transparent)`,
            left: '-100%',
            pointerEvents: 'none', zIndex: 0,
            '.MuiCard-root:hover &': { animation: 'card-shimmer 0.7s ease-out forwards' },
          }} />

          {/* Corner glow */}
          <Box sx={{
            position: 'absolute', top: -20, left: -20,
            width: 90, height: 90, borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(C.neon, isActive ? 0.18 : 0.08)} 0%, transparent 70%)`,
            pointerEvents: 'none', zIndex: 0,
            transition: 'all 0.3s ease',
          }} />

          {/* Active radial wash */}
          {isActive && (
            <Box sx={{
              position: 'absolute', inset: 0,
              background: `radial-gradient(ellipse at 15% 20%, ${alpha(C.neon, 0.1)} 0%, transparent 60%)`,
              pointerEvents: 'none', zIndex: 0,
            }} />
          )}

          {/* Top-left corner ticks */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0,
            width: 20, height: 20,
            pointerEvents: 'none', zIndex: 2,
          }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: 2, height: 12, background: `linear-gradient(180deg, ${C.neon}, transparent)`, borderRadius: 1 }} />
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: 12, height: 2, background: `linear-gradient(90deg, ${C.neon}, transparent)`, borderRadius: 1 }} />
          </Box>

          <CardContent sx={{
            p: { xs: 1.75, sm: 2.25, md: 2.5 },
            position: 'relative', zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.25, sm: 1.5, md: 2 },
            minWidth: 0,
            ...(progressValue == null ? { '&:last-child': { pb: { xs: 1.75, sm: 2.25, md: 2.5 } } } : {}),
          }}>
            {/* Icon */}
            {Icon && <IconBox Icon={Icon} C={C} isActive={isActive} index={index} />}

            {/* Text */}
            <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <motion.div
                key={displayValue}
                initial={{ opacity: 0, scale: 0.85, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <Typography sx={{
                  fontSize: valueSize === 'xsmall'
                    ? { xs: '0.72rem', sm: '0.82rem' }
                    : valueSize === 'small'
                    ? { xs: '0.95rem', sm: '1.05rem' }
                    : { xs: '1.7rem', sm: '1.9rem' },
                  lineHeight: valueSize === 'small' || valueSize === 'xsmall' ? 1.2 : 1,
                  letterSpacing: valueSize === 'small' || valueSize === 'xsmall' ? '-0.01em' : '-0.03em',
                  fontFamily: valueSize === 'xsmall' ? "'JetBrains Mono', monospace" : "'Syne', sans-serif",
                  fontWeight: valueSize === 'xsmall' ? 600 : 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  background: `linear-gradient(135deg, #fff 0%, ${C.neon} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: isActive ? `drop-shadow(0 0 8px ${alpha(C.neon, 0.5)})` : 'none',
                  transition: 'filter 0.3s ease',
                }}>
                  {displayValue}
                </Typography>
              </motion.div>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.6, minWidth: 0 }}>
                {/* Live dot */}
                <Box sx={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: C.neon,
                  boxShadow: `0 0 6px ${C.neon}`,
                  animation: 'ping-dot 2s ease-in-out infinite',
                  flexShrink: 0,
                  mt: 0.25,
                }} />
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: { xs: compact ? '0.5rem' : '0.56rem', sm: compact ? '0.52rem' : '0.6rem', md: compact ? '0.52rem' : '0.62rem' },
                  fontWeight: 700,
                  color: isActive ? alpha(C.neon, 0.85) : alpha(C.neon, 0.5),
                  letterSpacing: { xs: compact ? 0.7 : 1, sm: compact ? 0.9 : 1.35, md: compact ? 1.1 : 1.8 },
                  textTransform: 'uppercase',
                  lineHeight: 1.25,
                  transition: 'color 0.3s ease',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  minWidth: 0,
                }}>
                  {label}
                </Typography>
              </Box>
            </Box>

            {/* Trend badge */}
            {trend != null && (
              <motion.div
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.07 + 0.2 }}
                style={{ flexShrink: 0 }}
              >
                <Box sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  px: 0.8, py: 0.5,
                  borderRadius: '8px',
                  background: trend >= 0 ? alpha('#38BDF8', 0.1) : alpha('#818CF8', 0.1),
                  border: `1px solid ${trend >= 0 ? alpha('#38BDF8', 0.3) : alpha('#818CF8', 0.3)}`,
                  animation: 'float-chip 3s ease-in-out infinite',
                }}>
                  <Typography sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.7rem', fontWeight: 700, lineHeight: 1,
                    color: trend >= 0 ? '#38BDF8' : '#818CF8',
                  }}>
                    {trend >= 0 ? '↑' : '↓'}
                  </Typography>
                  <Typography sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.65rem', fontWeight: 700, lineHeight: 1,
                    color: trend >= 0 ? '#38BDF8' : '#818CF8',
                    mt: 0.2,
                  }}>
                    {Math.abs(trend)}%
                  </Typography>
                </Box>
              </motion.div>
            )}
          </CardContent>

          {/* Progress bar */}
          {progressValue != null && (
            <Box sx={{ px: 2.5, pb: 2, position: 'relative', zIndex: 1 }}>
              <Box sx={{
                height: 5, borderRadius: '99px',
                background: alpha(C.neon, 0.1),
                overflow: 'hidden',
                border: `1px solid ${alpha(C.neon, 0.15)}`,
              }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(Math.max(progressValue, 0), 100)}%` }}
                  transition={{ duration: 1.1, delay: index * 0.07 + 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    height: '100%', borderRadius: '99px',
                    background: `linear-gradient(90deg, ${C.neon}, ${C.grad[1]})`,
                    boxShadow: `0 0 10px ${alpha(C.neon, 0.6)}, 0 0 4px ${alpha(C.neon, 0.4)}`,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.58rem', fontWeight: 600,
                  color: alpha(C.neon, 0.45),
                }}>
                  PROGRESS
                </Typography>
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.58rem', fontWeight: 700,
                  color: alpha(C.neon, 0.7),
                }}>
                  {progressValue}%
                </Typography>
              </Box>
            </Box>
          )}
        </Card>
      </TiltCard>
    </motion.div>
    </Box>
  );
};

// ─── StatCards ────────────────────────────────────────────────────────────────
const StatCards = ({ stats = [], sx = {}, onCardClick, activeIndex = null, compact = false }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      lg: `repeat(${stats.length || 1}, minmax(0, 1fr))`,
    },
    gap: { xs: 1.5, sm: 2, md: 2.5 },
    mb: 3,
    width: '100%',
    ...sx,
  }}>
    {stats.map((stat, i) => (
      <StatCard
        key={i}
        {...stat}
        index={i}
        compact={compact}
        onClick={typeof onCardClick === 'function' ? () => onCardClick(stat, i) : undefined}
        isActive={activeIndex === i}
      />
    ))}
  </Box>
);

export default StatCards;
